import { Pencil } from 'lucide-react';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { useEffect, useState } from 'react';
import { pageCount } from '~/lib/listParams';

import { LocalTime } from '~/components/common/LocalTime';
import { ComboField } from '~/components/form';
import { StatTile, StatTileSkeleton, StatsRow } from '~/components/dashboard';
import { ListToolbar } from '~/components/layout';
import {
  Button,
  Card,
  CardBody,
  StatusChip,
  DataTable,
  EmptyState,
  FilterDrawer,
  Pagination,
  RowActionsMenu,
  type DataTableColumn,
} from '~/components/ui';
import { useDebouncedQuery } from '~/hooks/useDebouncedQuery';
import { REFRESH_MS } from '~/hooks/useAutoRefresh';
import { useListUrlState } from '~/hooks/useListUrlState';
import { useListFlagsQuery, useListFlagUsersQuery } from '~/modules/query/featureFlags';

import { USERS_PAGE_SIZE } from '~/components/Constants/featureFlagsConstants';
import { orDash, useLegacyOffset } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { UsersOverview } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

type UserRow = UsersOverview['users'][number];

const FILTER_KEYS = ['product', 'environment', 'flag'] as const;

// The service pages users by offset with a fixed 50 and returns the total: the URL carries `page`, the query sends `offset = (page - 1) * 50`.
export const UsersTab = ({ reference }: { reference: Reference }) => {
  const { environments, products } = reference;
  const list = useListUrlState({
    filterKeys: FILTER_KEYS,
    pageSizes: [USERS_PAGE_SIZE],
    defaultPageSize: USERS_PAGE_SIZE,
  });
  const migrating = useLegacyOffset(USERS_PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false);
  const environment =
    environments.find((e) => e.key === list.filters.environment)?.key ??
    environments.find((e) => e.key === 'production')?.key ??
    environments[0]?.key ??
    '';
  const product =
    products.find((p) => p.key === list.filters.product)?.key ?? products[0]?.key ?? '';

  const flags = useListFlagsQuery();
  const flagOptionsFor = (productKey: string) =>
    (flags.data ?? []).filter(
      (f) => !productKey || f.products.length === 0 || f.products.includes(productKey),
    );
  const flagOptions = flagOptionsFor(product);
  const flag =
    flagOptions.find((f) => f.key === list.filters.flag)?.key ?? flagOptions[0]?.key ?? '';

  // Known users change as SDKs sync; refetch every 30 s while the tab is visible and nothing is being typed.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const update = () =>
      setPaused(
        document.visibilityState !== 'visible' ||
          !!document.activeElement?.matches('input,select,textarea'),
      );
    update();
    const onFocusOut = () => window.setTimeout(update, 0);
    document.addEventListener('visibilitychange', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('visibilitychange', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  const query = useDebouncedQuery(list.q.trim().slice(0, 200));
  const overview = useListFlagUsersQuery(
    { environment, product, query, offset: (list.page - 1) * USERS_PAGE_SIZE, flag },
    {
      skip: !environment || flags.isLoading || migrating,
      pollingInterval: paused ? 0 : REFRESH_MS,
    },
  );

  const data = overview.data;
  const loadError = flags.error ?? overview.error;
  const retry = () => {
    if (flags.error) flags.refetch();
    overview.refetch();
  };
  const productName = (key: string) => products.find((p) => p.key === key)?.name ?? key;

  const columns: DataTableColumn<UserRow>[] = [
    {
      id: 'user',
      header: 'User',
      cell: (u) => (
        <div>
          <p className="font-medium text-gray-800 dark:text-white/90">{u.name || u.userId}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{u.email || 'No email'}</p>
        </div>
      ),
    },
    { id: 'userId', header: 'User ID', cell: (u) => u.userId },
    { id: 'customerId', header: 'Customer ID', cell: (u) => orDash(u.customerId) },
    { id: 'phone', header: 'Phone', cell: (u) => orDash(u.phone) },
    { id: 'product', header: 'Product', cell: (u) => productName(u.product) },
    {
      id: 'feature',
      header: 'Feature',
      cell: (u) =>
        u.enabled === null ? (
          orDash(null)
        ) : (
          <StatusChip
            label={u.enabled ? 'On' : 'Off'}
            tone={u.enabled ? 'success' : 'neutral'}
            dot
          />
        ),
    },
    { id: 'lastSeen', header: 'Last seen', cell: (u) => <LocalTime value={u.lastSeen} /> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (u) => (
        <RowActionsMenu
          testId={`feature-flags-users-actions-${u.id}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.featureFlagsRecord('users', u.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <StatsRow ariaLabel="Known user totals" columns={3}>
        {data ? (
          <>
            <StatTile label="Known users" value={data.total} />
            <StatTile label="Feature on" value={data.on} />
            <StatTile label="Feature off" value={data.off} />
          </>
        ) : (
          <>
            <StatTileSkeleton />
            <StatTileSkeleton />
            <StatTileSkeleton />
          </>
        )}
      </StatsRow>

      <Card>
        <ListToolbar
          title="Known users"
          description={`${
            flag
              ? `Counts evaluate ${flag} using its current rules for users in its assigned products.`
              : 'Choose or create a flag to see who has it on.'
          } Refreshes every 30 seconds.`}
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search name, email, user or customer ID…',
            testId: 'feature-flags-users-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setShowFilters(true),
            onClear: list.clear,
            testId: 'feature-flags-users-filter-button',
          }}
        />
        {environments.length === 0 ? (
          <CardBody>
            <EmptyState
              title="Add an environment first"
              description="Users are tracked per environment. Add one on the Environments page."
              action={
                <Button variant="outline" href={routes.featureFlagsAdmin('environments')}>
                  Go to environments
                </Button>
              }
            />
          </CardBody>
        ) : (
          <>
            <CardBody className="p-0">
              <DataTable<UserRow>
                framed={false}
                testId="feature-flags-users-table"
                loading={!data && !loadError}
                refreshing={overview.isFetching && !!data}
                error={
                  loadError && !data
                    ? {
                        what: 'the users',
                        message: apiErrorMessage(loadError as ApiErrorInput),
                        onRetry: retry,
                      }
                    : undefined
                }
                emptyMessage="No users in this view."
                emptyDescription="Users appear when your SDK syncs their identity, or when an admin adds one."
                data={data?.users ?? []}
                rowKey={(u) => u.id}
                href={(u) => routes.featureFlagsRecord('users', u.id)}
                rowLabel={(u) => `Open ${u.name || u.userId}`}
                columns={columns}
              />
            </CardBody>
            {data && (
              <Pagination
                pageIndex={list.page - 1}
                pageSize={USERS_PAGE_SIZE}
                pageCount={pageCount(data.total, USERS_PAGE_SIZE)}
                totalRows={data.total}
                onPageChange={(index) => list.setPage(index + 1)}
              />
            )}
          </>
        )}
      </Card>

      <FilterDrawer<Record<string, string>>
        open={showFilters}
        onClose={() => setShowFilters(false)}
        value={{ product, environment, flag }}
        onApply={list.setFilters}
        onClear={list.clear}
        testId="feature-flags-users-filter-drawer"
      >
        {(draft, patch) => (
          <>
            <ComboField
              id="feature-flags-users-filter-product"
              testId="feature-flags-users-filter-product-select"
              label="Product"
              value={draft.product}
              options={products.map((p) => ({ value: p.key, label: p.name }))}
              searchable={false}
              onChange={(next) => patch({ product: next, flag: '' })}
            />
            <ComboField
              id="feature-flags-users-filter-environment"
              testId="feature-flags-users-filter-environment-select"
              label="Environment"
              value={draft.environment}
              options={environments.map((e) => ({ value: e.key, label: e.name }))}
              searchable={false}
              onChange={(next) => patch({ environment: next })}
            />
            <ComboField
              id="feature-flags-users-filter-flag"
              testId="feature-flags-users-filter-flag-select"
              label="Feature flag"
              value={draft.flag}
              options={flagOptionsFor(draft.product).map((f) => ({ value: f.key, label: f.key }))}
              searchable
              onChange={(next) => patch({ flag: next })}
            />
          </>
        )}
      </FilterDrawer>
    </>
  );
};

export default UsersTab;
